import axios from "axios";
import { readTokenSync } from "@/lib/tokenStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// 60-second default. Long enough for the slowest vision-model call (typical
// nutrition-scan wall clock ≈ 25s p99, coach responses ≤ 40s) but short enough
// that the UI's "analyzing…" spinner can't hang forever on a network stall.
// Individual heavier endpoints (e.g. big-photo Glow Up analyzer) can override
// via `api.post(url, body, { timeout: 90000 })` at the call site.
const DEFAULT_TIMEOUT_MS = 60_000;

// withCredentials MUST be false. Reason: on Capacitor iOS the page origin is
// `capacitor://localhost`. When axios sets `withCredentials: true`, WebKit
// requires the server to echo `Access-Control-Allow-Origin: capacitor://localhost`
// AND `Access-Control-Allow-Credentials: true`. Our Emergent-Ingress layer
// rewrites CORS to `Access-Control-Allow-Origin: *` (wildcard), which the
// browser then REJECTS as incompatible with credentials mode — surfacing as
// "Couldn't reach https://…/api" inside the native app while the same URL
// works in Safari (Safari uses same-origin, no preflight). Auth is carried by
// the `Authorization: Bearer <token>` header attached in the interceptor
// below (see also `tokenStore.js`); cookies were never load-bearing.
const api = axios.create({
  baseURL: API,
  withCredentials: false,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: { "Content-Type": "application/json" },
});

// Attach Authorization header from localStorage as a backup to cookies
// Also attach the user's local timezone offset (in minutes WEST of UTC, JS
// convention) as a header on every request — the backend uses this to compute
// "today" and streaks in the user's local day, not UTC.
api.interceptors.request.use((config) => {
  const token = readTokenSync();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  try {
    const tz = new Date().getTimezoneOffset(); // minutes WEST of UTC
    config.headers = config.headers || {};
    config.headers["X-TZ-Offset-Minutes"] = String(tz);
  } catch {
    /* ignore */
  }
  return config;
});

export function formatApiError(err) {
  // Axios-specific error shapes — these are the most common silent-failure paths
  // on iOS PWA and slow mobile networks. Translate each into human copy.
  if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
    return "That took too long. Please check your connection and try again.";
  }
  if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
    // ERR_NETWORK covers three very different situations on iOS Capacitor —
    // (a) genuine offline, (b) CORS block from the WebView, (c) TLS/DNS
    // failure reaching the backend. Distinguish them so we don't mask
    // a real config bug behind "You seem to be offline".
    const url = err?.config?.baseURL || err?.config?.url || "";
    const online = typeof navigator !== "undefined" ? navigator.onLine : true;
    if (!online) {
      return "You appear to be offline. Please check your connection and try again.";
    }
    // Online but request failed — almost always CORS or unreachable backend
    // on native. Surface something diagnosable to the user AND log the URL
    // to the console so `console.log`-tail debugging surfaces it.
    // eslint-disable-next-line no-console
    console.error("[api] network error reaching", url, err);
    return `Couldn't reach ${url || "the server"}. This is a configuration issue, not your connection.`;
  }
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  // Server responded but with no useful body — give a helpful default.
  if (status >= 500) {
    return "Nova is having a moment. Please try again in a few seconds.";
  }
  if (status === 401 || status === 403) {
    return typeof detail === "string" ? detail : "You need to sign in to continue.";
  }
  if (status === 429) {
    return "You're going a bit fast — please wait a moment and try again.";
  }
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
